import React, { useState } from "react";

import WizardHeader from "../../components/portal/WizardHeader";
import StepIndicator from "../../components/portal/StepIndicator";
import CountryServicesStep from "../../components/portal/CountryServicesStep";
import CompanyDetailsStep from "../../components/portal/CompanyDetailsStep";
import AIAssistantStep from "../../components/portal/AIAssistantStep";
import GenerateDocumentsStep from "../../components/portal/GenerateDocumentsStep";

import type {
  ChatMessage,
  CompanyDetails,
  CountryCode,
  WizardStep,
  WizardStepId,
} from "../../store/@types/portal";
import { emptyCompanyDetails } from "../../store/@types/portal";

const STEPS: WizardStep[] = [
  { id: "country", label: "Country & Services" },
  { id: "company", label: "Company Details" },
  { id: "ai", label: "AI Assistance" },
  { id: "generate", label: "Generate Documents" },
];

const ComplianceWizard: React.FC = () => {
  const [currentStepId, setCurrentStepId] = useState<WizardStepId>("country");
  const [countryCode, setCountryCode] = useState<CountryCode | "">("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [companyDetails, setCompanyDetails] =
    useState<CompanyDetails>(emptyCompanyDetails);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const handleCountryChange = (code: CountryCode | "") => {
    setCountryCode(code);
    // Services are country-specific, so clear any prior selection
    // whenever the country changes.
    setSelectedServiceIds([]);
  };

  const handleToggleService = (serviceId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId],
    );
  };

  const handleCompanyDetailsChange = (
    field: keyof CompanyDetails,
    value: string,
  ) => {
    setCompanyDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendMessage = (content: string) => {
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      content,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Placeholder response — wire this up to your AI backend.
    window.setTimeout(() => {
      const assistantMessage: ChatMessage = {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        content:
          "Let me look into that for you and get back with the specifics.",
      };
      setMessages((prev) => [...prev, assistantMessage]);
    }, 500);
  };

  const handlePay = () => {
    // Wire this up to your payment provider, then trigger document generation.
    console.log("Proceeding to payment", {
      countryCode,
      selectedServiceIds,
      companyDetails,
    });
  };

  return (
    <div className="pb-20">
      <section className="py-12 md:py-24 lg:py-32 bg-gradient-to-br from-[#0B1526] via-[#0B1526] to-[#0F2A20]">
        <WizardHeader className="pt-20" />
      </section>

      <div className="px-4">
        <StepIndicator steps={STEPS} currentStepId={currentStepId} />
      </div>

      <div className="mx-auto mt-8 max-w-6xl px-4 sm:px-0">
        {currentStepId === "country" && (
          <CountryServicesStep
            countryCode={countryCode}
            selectedServiceIds={selectedServiceIds}
            onCountryChange={handleCountryChange}
            onToggleService={handleToggleService}
            onContinue={() => setCurrentStepId("company")}
          />
        )}

        {currentStepId === "company" && countryCode && (
          <CompanyDetailsStep
            countryCode={countryCode}
            details={companyDetails}
            onChange={handleCompanyDetailsChange}
            onBack={() => setCurrentStepId("country")}
            onContinue={() => setCurrentStepId("ai")}
          />
        )}

        {currentStepId === "ai" && countryCode && (
          <AIAssistantStep
            countryCode={countryCode}
            messages={messages}
            onSendMessage={handleSendMessage}
            onBack={() => setCurrentStepId("company")}
            onContinue={() => setCurrentStepId("generate")}
          />
        )}

        {currentStepId === "generate" && countryCode && (
          <GenerateDocumentsStep
            countryCode={countryCode}
            selectedServiceIds={selectedServiceIds}
            companyDetails={companyDetails}
            onBack={() => setCurrentStepId("ai")}
            onPay={handlePay}
          />
        )}
      </div>
    </div>
  );
};

export default ComplianceWizard;
